import React, { useContext, useEffect, useRef, useState } from "react";
import {
	HiOutlineBookOpen,
	HiOutlineCodeBracket,
	HiOutlineComputerDesktop,
	HiOutlinePencilSquare,
} from "react-icons/hi2";
import { AuthContext } from "../context/AuthContext";
import { getApi } from "../services/api";
import DetailCourseCard from "../components/common/cards/DetailCourseCard";
import { useSearchParams } from "react-router-dom";
import LoadingCard from "../components/common/Loadinganimations/LoadingCards";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const Enroll = () => {
	const [searchParams] = useSearchParams();
	const [activeCategory, setActiveCategory] = useState("");
	const [categories, setCategories] = useState([]);
	const [courses, setCourses] = useState([]);
	const [loading, setLoading] = useState(false);
	const { login } = useContext(AuthContext);
	const [showLeftButton, setShowLeftButton] = useState(false);
	const [showRightButton, setShowRightButton] = useState(true);
	const filterRef = useRef(null);

	useEffect(() => {
		const title = searchParams.get("title");

		const fetchCategories = async () => {
			try {
				const response = await getApi(`/courseCategory`);
				if (response && !response.error) {
					setCategories(response); // Categories already sorted by backend

					// Set the active category based on query params or the first category
					if (title) {
						setActiveCategory(title);
					} else if (response.length > 0) {
						setActiveCategory(response[0].title);
					}
				} else {
					console.error(
						"Error fetching categories:",
						response.message || "Unknown error"
					);
				}
			} catch (error) {
				console.error("Error fetching categories:", error.message);
			}
		};

		fetchCategories();
	}, [login, searchParams]);

	// Fetch courses based on the active category
	useEffect(() => {
		const fetchCourses = async () => {
			setLoading(true);
			try {
				const response = await getApi(
					`/courses?filter[category]=${activeCategory}`
				);
				if (response && !response.error) {
					setCourses(response); // Set fetched courses
				} else {
					console.error(
						"Error fetching courses:",
						response.message || "Unknown error"
					);
				}
			} catch (error) {
				console.error("Error fetching courses:", error.message);
			} finally {
				setLoading(false); // End loading
			}
		};

		if (activeCategory) {
			fetchCourses();
		}
	}, [activeCategory]);

	// Map category names to Hero Icons
	const categoryIcons = {
		Language: <HiOutlineBookOpen className="inline-block text-xl mr-2" />,
		Technology: (
			<HiOutlineComputerDesktop className="inline-block text-xl mr-2" />
		),
		"Home Tutoring": (
			<HiOutlineCodeBracket className="inline-block text-xl mr-2" />
		),
		"Arabic Books": (
			<HiOutlinePencilSquare className="inline-block text-xl mr-2" />
		),
	};

	const handleScroll = () => {
		const filterContainer = filterRef.current;
		const maxScrollLeft =
			filterContainer.scrollWidth - filterContainer.clientWidth;

		const scrollTolerance = 5;

		setShowLeftButton(filterContainer.scrollLeft > 0);
		setShowRightButton(
			filterContainer.scrollLeft < maxScrollLeft - scrollTolerance
		);
	};

	const scrollLeft = () => {
		filterRef.current.scrollBy({
			left: -200,
			behavior: "smooth",
		});
	};

	const scrollRight = () => {
		filterRef.current.scrollBy({
			left: 200,
			behavior: "smooth",
		});
	};

	useEffect(() => {
		handleScroll();
	}, []);

	return (
		<div className="min-h-screen px-6 lg:px-8 text-black bg-white mt-10">
			{/* Tab Navigation */}
			<div className="relative lg:mt-5 mb-14 lg:px-3 flex justify-center items-center">
				{/* Left Scroll Button */}
				{showLeftButton && (
					<div className="lg:hidden absolute flex items-center inset-y-0 -left-5 w-20 bg-gradient-to-r from-white dark:from-gray-800 via-white dark:via-gray-800 to-transparent">
						<button
							className="absolute flex justify-end left-[1rem] text-center bg-gray-300 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 p-1 rounded-full text-gray-600 dark:text-gray-300 text-sm hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer"
							onClick={scrollLeft}
						>
							<FaChevronLeft />
						</button>
					</div>
				)}

				{/* Right Scroll Button */}
				{showRightButton && (
					<div className="lg:hidden absolute flex items-center inset-y-0 -right-[0.1rem] w-10 bg-gradient-to-l from-white dark:from-gray-800 via-white dark:via-gray-800 to-transparent">
						<button
							className="absolute flex justify-end -right-2 text-center bg-gray-300 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 p-1 rounded-full text-gray-600 dark:text-gray-300 text-sm hover:text-gray-700 dark:hover:text-gray-200 hover:cursor-pointer"
							onClick={scrollRight}
						>
							<FaChevronRight />
						</button>
					</div>
				)}

				{/* Scrollable Categories */}
				<div className="flex justify-center items-center w-full max-w-full">
					<div
						className="overflow-x-auto no-scrollbar w-auto"
						onScroll={handleScroll}
						ref={filterRef}
					>
						<div className="flex gap-3 my-1 justify-start w-auto">
							{categories.map((category) => (
								<button
									key={category._id}
									className={`flex items-center px-6 lg:py-2 py-4 rounded-full text-sm font-semibold transition duration-300 whitespace-nowrap ${
										activeCategory === category.title
											? "bg-blue-600 text-white border border-blue-700"
											: "bg-gray-200 text-gray-800 hover:bg-blue-600 hover:text-white hover:border-blue-700"
									}`}
									onClick={() =>
										setActiveCategory(category.title)
									}
								>
									{categoryIcons[category.title]}{" "}
									{category.title}
								</button>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* Course Cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:gap-6 gap-16">
				{loading ? (
					[1, 2, 3, 4].map((_, index) => <LoadingCard key={index} />)
				) : courses.length > 0 ? (
					courses.map((course) => (
						<DetailCourseCard key={course._id} course={course} />
					))
				) : (
					<div className="col-span-1 sm:col-span-2 md:col-span-4 text-center">
						<p className="text-gray-500">
							No courses available for this category.
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default Enroll;
